<template>
  <label ref="segmentRef" class="free-segment free-segment-text-colors">
    <input
      class="hidden"
      type="radio"
      :checked="shouldBeActive()"
      :name="name"
      :disabled="disabled"
      @change="emit('onChange', id)"
      @click.stop="emit('onClick')"
    />
    <div
      :class="{
        '--active': shouldBeActive(),
        'opacity-25': disabled,
      }"
    >
      <slot />
    </div>
  </label>
</template>

<script setup lang="ts">
  import { ref, watch, inject, onMounted } from 'vue';

  type UpdatePointerPosition = (el: DOMRect, element?: HTMLElement) => void;
  type IsActive = (id: string) => boolean;

  const props = defineProps<{
    id: string;
    name: string;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    onChange: [id: string];
    onClick: [];
  }>();

  const isActive = inject<IsActive>('isActive');
  const updatePointerPosition = inject<UpdatePointerPosition>(
    'updatePointerPosition',
  );

  const segmentRef = ref<HTMLLabelElement | null>(null);

  function shouldBeActive() {
    if (isActive) {
      return isActive(props.id);
    }
    throw new Error('ERR_INJECTION_NOT_FOUND: isActive');
  }

  function setActive() {
    if (shouldBeActive() && segmentRef.value && updatePointerPosition) {
      updatePointerPosition(
        segmentRef.value.getBoundingClientRect(),
        segmentRef.value,
      );
    }
  }

  watch(() => shouldBeActive(), setActive);

  onMounted(() => setTimeout(setActive, 100));
</script>

<style scoped>
  .free-segment {
    @apply truncate rounded-xl p-2 text-center text-sm font-medium;
    z-index: inherit;
  }

  .free-segment-text-colors {
    --free-segment-active-color: rgb(var(--text-neutral-primary));
    --free-segment-inactive-color: rgb(var(--text-neutral-tertiary));
    color: var(--free-segment-inactive-color);
  }

  .--active {
    color: var(--free-segment-active-color);
  }
</style>
